import { ActionIcon, Box, Group, Popover, Text, TextInput, UnstyledButton } from "@mantine/core"
import { CaretDownIcon, CaretRightIcon, FolderIcon, FolderOpenIcon, XIcon } from "@phosphor-icons/react";
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import classes from "./category-tree-select.module.css"

interface CategoryItem {
  id: string
  name: string
  parentId?: string | null
}

interface TreeNode {
  id: string
  name: string
  parentId?: string | null
  children: TreeNode[]
}

interface Props {
  data: CategoryItem[]
  value: string | null
  onChange: (value: string | null) => void
  /** Field label; defaults to the generic "all" text the filter bars used before. */
  label?: string
}

export function buildTree(items: CategoryItem[]): TreeNode[] {
  const map = new Map<string, TreeNode>()
  const roots: TreeNode[] = []

  for (const item of items) {
    map.set(item.id, { ...item, children: [] })
  }

  for (const item of items) {
    const node = map.get(item.id)!
    if (item.parentId && map.has(item.parentId)) {
      map.get(item.parentId)!.children.push(node)
    } else {
      roots.push(node)
    }
  }

  const sort = (nodes: TreeNode[]) => {
    nodes.sort((a, b) => a.name.localeCompare(b.name))
    nodes.forEach((n) => sort(n.children))
  }
  sort(roots)

  return roots
}

function getAllDescendantIds(node: TreeNode): string[] {
  const ids: string[] = [node.id]
  for (const child of node.children) {
    ids.push(...getAllDescendantIds(child))
  }
  return ids
}

export function getDescendantIds(items: CategoryItem[], selectedId: string): string[] {
  const tree = buildTree(items)
  const node = findNodeById(tree, selectedId)
  if (!node) return [selectedId]
  return getAllDescendantIds(node)
}

function findNodeById(nodes: TreeNode[], id: string): TreeNode | null {
  for (const node of nodes) {
    if (node.id === id) return node
    const found = findNodeById(node.children, id)
    if (found) return found
  }
  return null
}

function findLabelById(nodes: TreeNode[], id: string): string | null {
  const node = findNodeById(nodes, id)
  return node?.name ?? null
}

function TreeNodeItem({
  node,
  depth,
  selectedId,
  expandedIds,
  onToggleExpand,
  onSelect,
}: {
  node: TreeNode
  depth: number
  selectedId: string | null
  expandedIds: Set<string>
  onToggleExpand: (id: string) => void
  onSelect: (id: string) => void
}) {
  const hasChildren = node.children.length > 0
  const isExpanded = expandedIds.has(node.id)
  const isSelected = selectedId === node.id

  return (
    <>
      <UnstyledButton
        className={classes.node}
        data-selected={isSelected || undefined}
        style={{ paddingLeft: 8 + depth * 20 }}
        onClick={() => onSelect(node.id)}
      >
        <Group gap={4} wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          {hasChildren ? (
            <ActionIcon
              size="xs"
              variant="subtle"
              color="gray"
              onClick={(e) => {
                e.stopPropagation()
                onToggleExpand(node.id)
              }}
            >
              {isExpanded ? <CaretDownIcon size={12} /> : <CaretRightIcon size={12} />}
            </ActionIcon>
          ) : (
            <Box style={{ width: 18 }} />
          )}
          {hasChildren ? (
            isExpanded ? <FolderOpenIcon size={14} color="var(--mantine-color-brand-6)" /> : <FolderIcon size={14} color="var(--mantine-color-gray-5)" />
          ) : (
            <Box style={{ width: 14 }} />
          )}
          <Text size="sm" truncate style={{ flex: 1 }}>{node.name}</Text>
          {hasChildren && (
            <Text size="sm" c="dimmed" fw={400}>({node.children.length})</Text>
          )}
        </Group>
      </UnstyledButton>
      {hasChildren && isExpanded && node.children.map((child) => (
        <TreeNodeItem
          key={child.id}
          node={child}
          depth={depth + 1}
          selectedId={selectedId}
          expandedIds={expandedIds}
          onToggleExpand={onToggleExpand}
          onSelect={onSelect}
        />
      ))}
    </>
  )
}

export function CategoryTreeSelect({ data, value, onChange, label }: Props) {
  const { t } = useTranslation("common")
  const [opened, setOpened] = useState(false)
  const [search, setSearch] = useState("")
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  const tree = useMemo(() => buildTree(data), [data])

  const selectedLabel = value ? findLabelById(tree, value) : null

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleSelect = (id: string) => {
    onChange(id === value ? null : id)
    setOpened(false)
    setSearch("")
  }

  const filteredTree = useMemo(() => {
    if (!search.trim()) return tree

    const q = search.trim().toLowerCase()
    const matchingIds = new Set<string>()
    const parentIds = new Set<string>()

    const collectMatches = (nodes: TreeNode[]) => {
      for (const node of nodes) {
        if (node.name.toLowerCase().includes(q)) {
          matchingIds.add(node.id)
          let current = node.parentId
          while (current) {
            parentIds.add(current)
            const parent = findNodeById(tree, current)
            current = parent?.parentId ?? null
          }
        }
        collectMatches(node.children)
      }
    }
    collectMatches(tree)

    const filterNodes = (nodes: TreeNode[]): TreeNode[] => {
      return nodes
        .filter((n) => matchingIds.has(n.id) || parentIds.has(n.id))
        .map((n) => ({
          ...n,
          children: filterNodes(n.children),
        }))
    }
    return filterNodes(tree)
  }, [tree, search])

  const searchExpandedIds = useMemo(() => {
    if (!search.trim()) return expandedIds
    const ids = new Set<string>()
    const expand = (nodes: TreeNode[]) => {
      for (const node of nodes) {
        if (node.children.length > 0) {
          ids.add(node.id)
          expand(node.children)
        }
      }
    }
    expand(filteredTree)
    return ids
  }, [filteredTree, search, expandedIds])

  return (
    <Box>
      <Text size="sm" fw={500} mb={2}>{label ?? t("filter.all")}</Text>
      <Popover
        opened={opened}
        onChange={setOpened}
        position="bottom-start"
        width={320}
        shadow="md"
      >
        <Popover.Target>
          <UnstyledButton
            className={classes.trigger}
            onClick={() => setOpened((o) => !o)}
          >
            <Text size="sm" c={selectedLabel ? undefined : "dimmed"} truncate style={{ flex: 1 }}>
              {selectedLabel || t("filter.all")}
            </Text>
            {value ? (
              <ActionIcon
                size="xs"
                variant="subtle"
                color="gray"
                onClick={(e) => {
                  e.stopPropagation()
                  onChange(null)
                }}
              >
                <XIcon size={12} />
              </ActionIcon>
            ) : (
              <CaretDownIcon size={14} color="var(--mantine-color-gray-5)" />
            )}
          </UnstyledButton>
        </Popover.Target>

        <Popover.Dropdown p={0}>
          <Box p="xs" pb={0}>
            <TextInput
              placeholder={t("filter.searchPlaceholder")}
              size="sm"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              rightSection={search ? (
                <ActionIcon size="xs" variant="subtle" onClick={() => setSearch("")}>
                  <XIcon size={12} />
                </ActionIcon>
              ) : null}
            />
          </Box>
          <Box className={classes.treeContainer}>
            {filteredTree.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center" py="md">{t("noData")}</Text>
            ) : (
              filteredTree.map((node) => (
                <TreeNodeItem
                  key={node.id}
                  node={node}
                  depth={0}
                  selectedId={value}
                  expandedIds={search.trim() ? searchExpandedIds : expandedIds}
                  onToggleExpand={toggleExpand}
                  onSelect={handleSelect}
                />
              ))
            )}
          </Box>
        </Popover.Dropdown>
      </Popover>
    </Box>
  )
}

